import "./AiFormeLandingPage.scss";

import { Breadcrumbs, CrumbProps } from "@amzn/stencil-react-components/breadcrumbs";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunction, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { AiForMeHeaderBanner } from "common/components/AIForMe/AiForMeHeaderBanner";
import { CompareBar, CompareFlyout } from "common/components/AIForMe/Compare";
import { useAiForMeSearchList } from "common/components/AIForMe/useAiForMeSearchList";
import { CustomerFeedbackBanner } from "common/components/CustomerFeedback/Banner/CustomerFeedbackBanner";
import { ModalWhatsNew } from "common/components/ModalWhatsNew/ModalWhatsNew";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { PolicyModal } from "common/components/PolicyModal/PolicyModal";
import { CompareProvider, useCompare } from "common/context/compare";
import { useLists } from "common/context/lists/ListsContext";
import { createTrackClickInteraction, fetchRecommendedTools, getJobFamilyNameFromMap } from "common/helpers";
import { COOKIE_NAMES, getCookie, setCookie } from "common/helpers/cookie-utils";
import { useAiForMeFeedbacks } from "common/hooks";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool } from "lib/models/aiforme-models";
import { AiForMeNews } from "lib/models/aiformenews-models";
import { CatalogicalService } from "lib/services";
import { FilteringOptions, ItemModel } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";
import { RecentlyVisitedService } from "lib/services/recently-visited-service";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { AiExplorationSection } from "./components/AiExplorationSection";
import { AiNewsSection } from "./components/AiNewsSection";
import { FeaturedToolsSection } from "./components/FeaturedToolsSection";
import { RecentlyUsedSection, RecentlyUsedTool } from "./components/RecentlyUsedSection";
import { TrendingToolsSection } from "./components/TrendingToolsSection";

type SortOption = {
  field: string[];
  order: ("asc" | "desc")[];
};

const aiNewsSortingOption: SortOption = {
  field: ["sortOrder", "catalogicalGenerated.created.on"],
  order: ["asc", "desc"],
};

const NO_DATA_NO_LOADING = false;
const AMAZON_PICKED_LIST_ID = "lst::items/7f1ccfde-0a19-4352-8d5f-3fdcc2cc8133";
const TIMEOUT_TO_DISPLAY_DISCLAIMER = 500; // ms

// temporary get items to fill data
/* istanbul ignore next */
const GetItems = async <T extends ItemModel>(
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  client: CatalogicalService<T>,
  parameters: FilteringOptions = { sortBy: [], sortDirection: [], filters: [] }
) => {
  setIsLoading(true);
  try {
    const response = await client.getItems(parameters);
    setIsLoading(false);

    return response;
  } catch (err) {
    setIsLoading(false);
  }
};

const showLoadingComponent = (loadingMessagePathString: string) => {
  return (
    <Col alignItems="center" justifyContent="center" minHeight="400px">
      <Spinner size={SpinnerSize.Small} />
      <Text fontSize={"S100"}>{getString(loadingMessagePathString)}</Text>
    </Col>
  );
};

/**
 * Inner component that uses compare context
 */
const AiFormeLandingPageContent: FC = () => {
  const [error, setError] = useState<string | null>(null);
  const history = useHistory();
  let openHandler: () => void | undefined;
  const { inputs, updateInputs, batchUpdateInputs } = useAiForMeSearchList();
  const [isLoadingRoleTools, setIsLoadingRoleTools] = useState<boolean>(true);
  const [isLoadingAiformeNews, setIsLoadingAiformeNews] = useState<boolean>(false);
  const catalogicalServiceClient = useMemo(() => new CatalogicalService<AIForMeTool>("aiforme"), []);
  const recentlyVisitedService = useMemo(() => new RecentlyVisitedService(), []);
  const aiNewsCatalogicalServiceClient = useMemo(() => new CatalogicalService<AiForMeNews>("aiformenews"), []);
  const csrfService = useMemo(() => new CSRFService(), []);
  const [jobFamilyName, setJobFamilyName] = useState<string | undefined>();
  const { evaluateLists, getListById } = useLists();

  const [aiformeNews, setAiformeNews] = useState<AiForMeNews[]>([]);

  // Role tools from evaluateLists based on job family
  const [roleTools, setRoleTools] = useState<AIForMeTool[]>([]);

  // Amazon-picked tools from curated list
  const [amazonPickedTools, setAmazonPickedTools] = useState<AIForMeTool[]>([]);
  const [isLoadingAmazonPicked, setIsLoadingAmazonPicked] = useState<boolean>(true);

  // Recently visited tools
  const [recentlyVisitedTools, setRecentlyVisitedTools] = useState<RecentlyUsedTool[]>([]);
  const [isLoadingRecentlyVisited, setIsLoadingRecentlyVisited] = useState<boolean>(true);

  useEffect(() => {
    // set document title (AI For Me (Tab Title))
    document.title = getString("aiForMe.title");
    // get user job family name
    const fetchJobFamily = async () => {
      try {
        const name = await getJobFamilyNameFromMap();
        setJobFamilyName(name);
      } catch (err) {
        setError("Failed to load user information");
        console.error("Error fetching job family:", err);
      }
    };
    fetchJobFamily();
  }, []);

  // Fetch role tools using evaluateLists when jobFamilyName is available
  useEffect(() => {
    if (jobFamilyName && evaluateLists) {
      setIsLoadingRoleTools(true);
      fetchRecommendedTools([jobFamilyName], evaluateLists, "All")
        .then((tools) => {
          setRoleTools(tools as AIForMeTool[]);
        })
        .catch((error) => {
          console.error("Error fetching role tools:", error);
          setRoleTools([]);
        })
        .finally(() => {
          setIsLoadingRoleTools(false);
        });
    } else if (!jobFamilyName) {
      setIsLoadingRoleTools(false);
    }
  }, [jobFamilyName, evaluateLists]);

  // Fetch amazon-picked tools from curated list
  useEffect(() => {
    setIsLoadingAmazonPicked(true);
    getListById(AMAZON_PICKED_LIST_ID)
      .then((list) => {
        setAmazonPickedTools((list.fullItems as AIForMeTool[]) || []);
      })
      .catch((error) => {
        console.error("Error fetching amazon picked tools:", error);
        setAmazonPickedTools([]);
      })
      .finally(() => {
        setIsLoadingAmazonPicked(false);
      });
  }, [getListById]);

  // Fetch recently visited items and resolve to full tool objects
  const fetchRecentlyVisited = useCallback(
    (showLoading = true) => {
      if (showLoading) setIsLoadingRecentlyVisited(true);
      recentlyVisitedService
        .getRecentlyVisited("aiforme", 9)
        .then(async (visits) => {
          if (visits.length === 0) {
            setRecentlyVisitedTools([]);
            return;
          }
          const response = await catalogicalServiceClient.getItems({
            filters: { taxonomyId: visits.map((v) => v.itemId) },
            sortBy: [],
            sortDirection: [],
          });
          const toolsMap = new Map((response?.data ?? []).map((t) => [t.taxonomyId, t]));
          const tools: RecentlyUsedTool[] = visits
            .filter((v) => toolsMap.has(v.itemId))
            .map((v) => ({ ...toolsMap.get(v.itemId)!, lastUsedDate: v.visitedAt }));
          setRecentlyVisitedTools(tools);
        })
        .catch((err) => {
          console.error("Error fetching recently visited tools:", err);
          if (showLoading) setRecentlyVisitedTools([]);
        })
        .finally(() => {
          if (showLoading) setIsLoadingRecentlyVisited(false);
        });
    },
    [recentlyVisitedService, catalogicalServiceClient]
  );

  const handleVisitRecorded = useCallback((tool: AIForMeTool, visitedAt: string) => {
    setRecentlyVisitedTools((prev) => {
      const filtered = prev.filter((t) => t.taxonomyId !== tool.taxonomyId);
      return [{ ...tool, lastUsedDate: visitedAt }, ...filtered].slice(0, 9);
    });
  }, []);

  useEffect(() => {
    fetchRecentlyVisited();
  }, [fetchRecentlyVisited]);

  const renderModal: RenderModalFunction = ({ close }: RenderModalFunctionParams) => (
    <PolicyModal
      policy={getString("policy.ai.policies")}
      policyTitle={getString("policy.ai.modal.title")}
      policyType="ai"
      close={close}
    />
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (openHandler && !getCookie(COOKIE_NAMES.AI_POLICY_ACKNOWLEDGED)) {
        openHandler();
      } else {
        setCookie(COOKIE_NAMES.AI_POLICY_ACKNOWLEDGED, "true", 365);
      }
    }, TIMEOUT_TO_DISPLAY_DISCLAIMER);

    return () => clearTimeout(timeoutId);
  }, []);

  const { userFeedbacks, voteHandler } = useAiForMeFeedbacks(catalogicalServiceClient, csrfService);

  // Compare functionality
  const { selectedTools, canCompare, isFlyoutOpen, openFlyout, closeFlyout, clearAll, removeTool, isReviewFlyoutOpen } =
    useCompare();

  const trackClickInteraction = useMemo(
    () => createTrackClickInteraction(catalogicalServiceClient, csrfService),
    [catalogicalServiceClient, csrfService]
  );

  const isSectionReady = process.env.sectionReadyTestHelper === "true";

  useEffect(() => {
    GetItems(setIsLoadingAiformeNews, aiNewsCatalogicalServiceClient, {
      sortBy: aiNewsSortingOption.field,
      sortDirection: aiNewsSortingOption.order,
      filters: {},
    }).then((response) => {
      if (response) {
        setAiformeNews(response.data || []);
      }
    });
  }, [aiNewsCatalogicalServiceClient]);

  return (
    <WithModal renderModal={renderModal} shouldCloseOnClickOutside={false}>
      {({ open }) => (
        <>
          <Col className="aiforme-landing-page">
            <div className="aiforme-landing-page__gradient-back">
              <ModalWhatsNew />
              <PageMainContainer>
                <Row>
                  <Breadcrumbs
                    crumbs={[
                      {
                        title: getString("logoText"),
                        url: "/",
                      },
                      {
                        title: getString("aiForMe.title"),
                      },
                    ]}
                    onCrumbClick={
                      /* istanbul ignore next */
                      (e, crumb: CrumbProps) => {
                        if (crumb.url) {
                          e.preventDefault();
                          history.push(crumb.url);
                        }
                      }
                    }
                  />
                </Row>
                <AiForMeHeaderBanner
                  inputs={inputs}
                  updateInputs={updateInputs}
                  batchUpdateInputs={batchUpdateInputs}
                  isNewLandingPage={true}
                ></AiForMeHeaderBanner>
              </PageMainContainer>
            </div>
            <div className="aiforme-landing-page__content">
              <PageMainContainer>
                {isLoadingRoleTools || isLoadingAmazonPicked ? (
                  showLoadingComponent("aiForMe.landingPage.featuredToolsSection.loadingMessage")
                ) : (
                  <Col margin={"3rem 0"}>
                    <FeaturedToolsSection
                      roleTools={roleTools}
                      orgTools={[]} // TOODO: replace with org tools (when available)
                      amazonPickedTools={amazonPickedTools}
                      userRole={jobFamilyName || ""}
                      voteHandler={voteHandler}
                      client={catalogicalServiceClient}
                      userFeedbacks={userFeedbacks}
                      onTrackClick={trackClickInteraction}
                      onVisitRecorded={handleVisitRecorded}
                    />
                  </Col>
                )}

                {isLoadingAiformeNews ? (
                  showLoadingComponent("aiForMe.landingPage.aiNewsSection.loadingMessage")
                ) : aiformeNews.length > 0 ? (
                  <Col margin={"3rem 0"}>
                    <AiNewsSection news={aiformeNews} />
                  </Col>
                ) : (
                  <Col margin={"3rem 0"}>
                    <AiExplorationSection />
                  </Col>
                )}

                {isLoadingRecentlyVisited ? (
                  showLoadingComponent("aiForMe.landingPage.recentlyUsedToolsSection.loadingMessage")
                ) : (
                  <Col margin={"3rem 0"}>
                    <RecentlyUsedSection
                      tools={recentlyVisitedTools}
                      voteHandler={voteHandler}
                      client={catalogicalServiceClient}
                      userFeedbacks={userFeedbacks}
                      onTrackClick={trackClickInteraction}
                      onVisitRecorded={handleVisitRecorded}
                    />
                  </Col>
                )}

                {isSectionReady &&
                  (NO_DATA_NO_LOADING ? (
                    showLoadingComponent("aiForMe.landingPage.weeklyTrendingToolsSection.loadingMessage")
                  ) : (
                    <Col margin={"3rem 0"}>
                      <TrendingToolsSection
                        tools={[]} // TODO: Replace with trending tools (when available)
                        voteHandler={voteHandler}
                        client={catalogicalServiceClient}
                        userFeedbacks={userFeedbacks}
                        onTrackClick={trackClickInteraction}
                        onVisitRecorded={handleVisitRecorded}
                      />
                    </Col>
                  ))}

                <Col margin={"3rem 0"}>
                  <CustomerFeedbackBanner touchpoint="aiforme-landing-page" />
                </Col>
              </PageMainContainer>
            </div>

            {/* Compare Bar - sticky bottom banner */}
            <CompareBar
              selectedTools={selectedTools}
              canCompare={canCompare}
              isComparing={isFlyoutOpen}
              isReviewFlyoutOpen={isReviewFlyoutOpen}
              onRemoveTool={removeTool}
              onCancel={clearAll}
              onCompareNow={openFlyout}
            />

            {/* Compare Flyout - modal with comparison table */}
            <CompareFlyout
              isOpen={isFlyoutOpen}
              tools={selectedTools}
              onClose={closeFlyout}
              onRemoveTool={removeTool}
            />
          </Col>

          {(() => {
            openHandler = open;
            return null;
          })()}
        </>
      )}
    </WithModal>
  );
};

/**
 * New AI for Me landing page with personalized experience.
 * Controlled by the TOGGLE_AI_FOR_ME_LANDING_PAGE feature flag.
 */
const AiFormeLandingPage: FC = () => {
  return (
    <CompareProvider>
      <AiFormeLandingPageContent />
    </CompareProvider>
  );
};

export default AiFormeLandingPage;
